import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ManutencaoGate } from "@/components/Manutencao";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmHost } from "@/components/ConfirmDialog";
import { AccessShareHost } from "@/components/AccessShare";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import EntrarComputador from "@/pages/EntrarComputador";
import Register from "@/pages/Register";
import PublicCourses from "@/pages/PublicCourses";
import Profissionais from "@/pages/Profissionais";
import About from "@/pages/About";
import ValidateCertificate from "@/pages/ValidateCertificate";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ServerStatus from "@/pages/admin/ServerStatus";
import { VersionWatcher } from "@/components/VersionWatcher";
import Trash from "@/pages/admin/Trash";
import Courses from "@/pages/admin/Courses";
import CourseEditor from "@/pages/admin/CourseEditor";
import Students from "@/pages/admin/Students";
import Enrollments from "@/pages/admin/Enrollments";
import Modules from "@/pages/admin/Modules";
import VideoLibrary from "@/pages/admin/VideoLibrary";
import Exams from "@/pages/admin/Exams";
import HelpVideos from "@/pages/admin/HelpVideos";
import EnrollmentDetail from "@/pages/admin/EnrollmentDetail";
import PayCharge from "@/pages/PayCharge";
import VideoRedirect from "@/pages/VideoRedirect";
import LessonEditor from "@/pages/admin/LessonEditor";
import PersonAdmin from "@/pages/admin/PersonAdmin";
import SalaoGestao from "@/pages/admin/SalaoGestao";
import Charges from "@/pages/admin/finance/Charges";
import Transactions from "@/pages/admin/finance/Transactions";
import Sales from "@/pages/admin/finance/Sales";
import Recipients from "@/pages/admin/finance/Recipients";
import Nfse from "@/pages/admin/finance/Nfse";
import SaleLinks from "@/pages/admin/finance/SaleLinks";
import BonusFees from "@/pages/admin/finance/BonusFees";
import Sale from "@/pages/Sale";
import ProfSaleLinks from "@/pages/instructor/ProfSaleLinks";
import ProfCharges from "@/pages/instructor/ProfCharges";
import FinanceSettings from "@/pages/admin/finance/FinanceSettings";
import SiteAdmin from "@/pages/admin/Site";
import Instructors from "@/pages/admin/Instructors";
import Groups from "@/pages/admin/Groups";
import GroupDetail from "@/pages/GroupDetail";
import GroupCourseReport from "@/pages/GroupCourseReport";
import InstructorLayout from "@/pages/instructor/InstructorLayout";
import DuvidasIA from "@/pages/instructor/DuvidasIA";
import TricolabApp from "@/pages/tricolab/TricolabApp";
import KeylaApp from "@/pages/KeylaApp";
import TricolabPainel from "@/pages/admin/TricolabPainel";
import InstructorGroups from "@/pages/instructor/InstructorGroups";
import ProfStudents from "@/pages/instructor/ProfStudents";
import StudentLayout from "@/pages/student/StudentLayout";
import Catalog from "@/pages/student/Catalog";
import Classroom from "@/pages/student/Classroom";
import InnovaIA from "@/pages/admin/InnovaIA";
import ExamPage from "@/pages/student/ExamPage";
import Certificate from "@/pages/student/Certificate";
import PaymentResult from "@/pages/student/PaymentResult";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import FirstAccess from "@/pages/FirstAccess";
import AccessLink from "@/pages/AccessLink";
import LegalPage from "@/pages/LegalPage";
import CursoCabeleireiro from "@/pages/CursoCabeleireiro";
import School from "@/pages/admin/School";
import Notices from "@/pages/admin/Notices";
import CertIssued, { CertIssuedView } from "@/pages/admin/CertIssued";
import CertTemplates from "@/pages/admin/CertTemplates";
import ShopAdmin from "@/pages/admin/ShopAdmin";
import { TermsGate } from "@/components/TermsGate";
import SignContract from "@/pages/student/SignContract";
import ValidateContract from "@/pages/ValidateContract";
import Contracts from "@/pages/admin/Contracts";
import { IdleWatcher } from "@/components/IdleWatcher";
import Profile from "@/pages/account/Profile";
import Account from "@/pages/account/Account";
import Messages from "@/pages/account/Messages";

// Páginas de conta dentro do layout do aluno (que não tem <main> com padding)
const StudentShell = () => (
  <main className="max-w-6xl mx-auto w-full p-5 md:p-8">
    <Outlet />
  </main>
);

function homeFor(role) {
  if (role === "admin") return "/admin";
  if (role === "instructor") return "/prof";
  return "/app";
}

/** Aluno com contrato pendente só vê a tela de assinatura (as páginas legais e a validação continuam abertas). */
function ContractGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const livre = /^\/(termos|privacidade|cabeleireiro|validar|validar-contrato)(\/|$)/.test(pathname);
  if (user && user.contract_pending && !user.onboarding_required && !user.impersonator && !livre) return <SignContract />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]"><Loader2 className="h-8 w-8 animate-spin text-[#232D4F]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_required) return <Navigate to="/primeiro-acesso" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

const ProfShell = () => <main className="max-w-6xl mx-auto w-full p-5 md:p-8"><Outlet /></main>;

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <VersionWatcher />
          <TermsGate />
          <IdleWatcher />
          <ContractGate>
          <ManutencaoGate>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/entrar-computador" element={<EntrarComputador />} />
            <Route path="/cursos" element={<PublicCourses />} />
            <Route path="/profissionais" element={<Profissionais />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/validar" element={<ValidateCertificate />} />
            <Route path="/validar-contrato" element={<ValidateContract />} />
            <Route path="/validar-contrato/:code" element={<ValidateContract />} />
            <Route path="/validar/:code" element={<ValidateCertificate />} />
            <Route path="/pagamento/retorno" element={<PaymentResult />} />
            <Route path="/pagar/:id" element={<PayCharge />} />
            <Route path="/video/assinatura" element={<VideoRedirect />} />
            <Route path="/comprar/:code" element={<Sale />} />
            <Route path="/cadastro/:token" element={<Register />} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/acesso/:token" element={<AccessLink />} />
            <Route path="/redefinir-senha/:token" element={<ResetPassword />} />
            <Route path="/termos" element={<LegalPage kind="termos" />} />
            <Route path="/privacidade" element={<LegalPage kind="privacidade" />} />
            <Route path="/cabeleireiro" element={<CursoCabeleireiro />} />
            <Route path="/tricolab" element={<TricolabApp />} />
            <Route path="/keyla" element={<KeylaApp />} />
            <Route path="/primeiro-acesso" element={<ProtectedRoute><FirstAccess /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="servidor" element={<ServerStatus />} />
              <Route path="salao" element={<SalaoGestao />} />
              <Route path="lixeira" element={<Trash />} />
              <Route path="cursos" element={<Courses />} />
              <Route path="innova-ia" element={<InnovaIA />} />
              <Route path="tricolab" element={<TricolabPainel />} />
              <Route path="modulos" element={<Modules />} />
              <Route path="biblioteca" element={<VideoLibrary />} />
              <Route path="provas" element={<Exams />} />
              <Route path="cursos/:id" element={<CourseEditor />} />
              <Route path="cursos/:id/ver" element={<Classroom preview />} />
              <Route path="cursos/:id/aulas/nova" element={<LessonEditor />} />
              <Route path="cursos/:id/aulas/:lid/editar" element={<LessonEditor />} />
              <Route path="alunos" element={<Students />} />
              <Route path="alunos/:id" element={<PersonAdmin kind="aluno" />} />
              <Route path="professores/:id" element={<PersonAdmin kind="professor" />} />
              <Route path="matriculas" element={<Enrollments />} />
              <Route path="matriculas/:sid/:cid" element={<EnrollmentDetail />} />
              <Route path="turmas" element={<Groups />} />
              <Route path="turmas/:id" element={<GroupDetail />} />
                <Route path="turmas/:id/curso/:cid" element={<GroupCourseReport />} />
              <Route path="professores" element={<Instructors />} />
              <Route path="loja" element={<ShopAdmin />} />
              <Route path="contratos" element={<Contracts />} />
              <Route path="certificados" element={<Navigate to="/admin/certificados/emitidos" replace />} />
              <Route path="certificados/emitidos" element={<CertIssued />} />
              <Route path="certificados/emitidos/:id" element={<CertIssuedView />} />
              <Route path="certificados/modelos" element={<CertTemplates />} />
              <Route path="site" element={<SiteAdmin />} />
              <Route path="integracoes" element={<Navigate to="/admin/financeiro/configuracoes" replace />} />
              <Route path="financeiro" element={<Navigate to="/admin/financeiro/vendas" replace />} />
              <Route path="financeiro/cobrancas" element={<Charges />} />
              <Route path="financeiro/transacoes" element={<Transactions />} />
              <Route path="financeiro/vendas" element={<Sales />} />
              <Route path="financeiro/links" element={<SaleLinks />} />
              <Route path="financeiro/bonus" element={<BonusFees />} />
              <Route path="financeiro/notas" element={<Nfse />} />
              <Route path="financeiro/recebedores" element={<Recipients />} />
              <Route path="financeiro/configuracoes" element={<FinanceSettings />} />
              <Route path="escola" element={<School />} />
              <Route path="videos-ajuda" element={<HelpVideos />} />
              <Route path="avisos" element={<Notices />} />
              <Route path="perfil" element={<Profile />} />
              <Route path="conta" element={<Account />} />
              <Route path="mensagens" element={<Messages />} />
            </Route>

            <Route path="/prof" element={<ProtectedRoute role="instructor"><InstructorLayout /></ProtectedRoute>}>
              <Route index element={<Catalog base="/prof" staff />} />
              <Route path="curso/:id" element={<Classroom preview backTo="/prof" />} />
              <Route path="cursos" element={<Navigate to="/prof" replace />} />
              <Route element={<ProfShell />}>
                <Route path="turmas" element={<InstructorGroups />} />
                <Route path="turmas/:id" element={<GroupDetail />} />
                <Route path="turmas/:id/curso/:cid" element={<GroupCourseReport />} />
                <Route path="alunos" element={<ProfStudents />} />
                <Route path="duvidas-ia" element={<DuvidasIA />} />
                <Route path="vendas" element={<ProfSaleLinks />} />
                <Route path="cobrancas" element={<ProfCharges />} />
                <Route path="perfil" element={<Profile />} />
                <Route path="conta" element={<Account />} />
                <Route path="mensagens" element={<Messages />} />
              </Route>
            </Route>

            <Route path="/assinatura-previa" element={<ProtectedRoute role="student"><SignContract preview /></ProtectedRoute>} />
            <Route path="/app" element={<ProtectedRoute role="student"><StudentLayout /></ProtectedRoute>}>
              <Route index element={<Catalog />} />
              <Route path="curso/:id" element={<Classroom />} />
              <Route path="curso/:id/prova" element={<ExamPage />} />
              <Route path="certificado/:id" element={<Certificate />} />
              <Route element={<StudentShell />}>
                <Route path="perfil" element={<Profile />} />
                <Route path="conta" element={<Account />} />
                <Route path="mensagens" element={<Messages />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ManutencaoGate>
          </ContractGate>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <ConfirmHost />
        <AccessShareHost />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
